import { useState } from 'react';
import { ShieldAlert, Wifi, X, Calendar } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLicenseStore } from '@/stores/licenseStore';

interface VerificationReminderProps {
  open: boolean;
  onVerify: () => Promise<void>;
  onDismiss: () => void;
}

/**
 * Shown 7 days before monthly verification is due
 * Non-blocking reminder to connect and verify license
 */
export function VerificationReminder({
  open,
  onVerify,
  onDismiss,
}: VerificationReminderProps) {
  const [isVerifying, setIsVerifying] = useState(false);
  const { verificationDueAt, license } = useLicenseStore();

  const daysUntilDue = verificationDueAt
    ? Math.ceil(
        (new Date(verificationDueAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
      )
    : 0;

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      await onVerify();
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => !isVerifying && onDismiss()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-100 rounded-full">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
            </div>
            <DialogTitle className="text-xl">
              Vérification de licence requise
            </DialogTitle>
          </div>
          <DialogDescription className="text-right text-sm text-muted-foreground mt-2">
            التحقق من الترخيص مطلوب
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Main message */}
          <Alert className="border-amber-200 bg-amber-50">
            <AlertDescription className="text-sm">
              <p className="font-medium text-amber-900 mb-2">
                Votre vérification mensuelle de licence est due dans{' '}
                <span className="font-bold">{daysUntilDue} jour(s)</span>.
              </p>
              <p className="text-amber-800">
                Pour continuer à utiliser leBusiness sans interruption, veuillez vous connecter
                à Internet et vérifier votre licence.
              </p>
            </AlertDescription>
          </Alert>

          {/* License info */}
          {license && license.license_key && (
            <div className="p-3 bg-muted rounded-lg space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type de licence:</span>
                <span className="font-medium uppercase">{license.license_type || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Clé de licence:</span>
                <code className="text-xs font-mono bg-background px-2 py-1 rounded">
                  {license.license_key?.substring(0, 16)}...
                </code>
              </div>
              {verificationDueAt && (
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">Date d'échéance:</span>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span className="font-medium">
                      {new Date(verificationDueAt).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Why monthly verification */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-900">
              <strong>Pourquoi une vérification mensuelle ?</strong>
              <br />
              La vérification mensuelle garantit la légitimité de votre licence et protège contre
              la contrefaçon. Cette mesure assure la qualité et la sécurité de notre logiciel pour
              tous les utilisateurs.
            </p>
          </div>

          {/* Arabic translation */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-right">
            <p className="text-xs text-gray-800 leading-relaxed">
              <strong>لماذا التحقق الشهري؟</strong>
              <br />
              يضمن التحقق الشهري شرعية ترخيصك ويحمي من التزوير. يضمن هذا الإجراء جودة وأمان برامجنا
              لجميع المستخدمين.
            </p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onDismiss} disabled={isVerifying} className="w-full sm:w-auto">
            <X className="w-4 h-4 mr-2" />
            Rappeler plus tard
          </Button>
          <Button
            onClick={handleVerify}
            disabled={isVerifying}
            className="w-full sm:w-auto bg-gradient-to-r from-emerald-500 to-teal-600"
          >
            {isVerifying ? (
              <>
                <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Vérification...
              </>
            ) : (
              <>
                <Wifi className="w-4 h-4 mr-2" />
                Vérifier maintenant
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
